import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';
import { useAppSelector } from '../../../../app/store';
import { useUpdateEncounterMutation } from '../../../../services/EncounterService';

interface UpdateEncounterButtonProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const UpdateEncounterButton: React.FC<UpdateEncounterButtonProps> = ({ onSuccess, onError }) => {
  const encounter = useAppSelector((state) => state.encounter.currentEncounter);
  const [updateEncounter, { isLoading }] = useUpdateEncounterMutation();

  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'CANCEL';

  const handleSubmit = async () => {
    if (!encounter) {
      onError?.('No encounter data available');
      return;
    }

    if (!encounter.id) {
      onError?.('Encounter ID is required for updates');
      return;
    }

    // Validate that encounter has at least one procedure
    if (!encounter.procedures || encounter.procedures.length === 0) {
      onError?.('At least one procedure is required before updating an encounter');
      return;
    }

    try {
      // Create a copy of the encounter and set serviceDate = admissionDate
      const updatedEncounter = {
        ...encounter,
        encounterInfo: {
          ...encounter.encounterInfo,
          visitNumber: encounter.encounterInfo?.visitNumber || 0,
          serviceDate: encounter.encounterInfo?.admissionDate || encounter.encounterInfo?.serviceDate
        }
      };
      
      await updateEncounter({ encounterView: updatedEncounter }).unwrap();
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = err?.data?.message || 'Failed to update encounter.';
      onError?.(errorMessage);
    }
  };

  return (
    <Button
      variant="contained"
      onClick={handleSubmit}
      disabled={isLoading || !encounter || isEncounterCancelled}
      sx={{
        backgroundColor: isEncounterCancelled ? '#9e9e9e' : '#162346',
        color: 'white',
        fontWeight: 600,
        px: 3,
        py: 1,
        '&:hover': {
          backgroundColor: isEncounterCancelled ? '#9e9e9e' : '#1a2a52'
        },
        '&:disabled': {
          backgroundColor: '#9e9e9e',
          color: 'rgba(255, 255, 255, 0.6)'
        }
      }}
    >
      {isLoading ? <CircularProgress size={20} color="inherit" /> : 
       isEncounterCancelled ? 'Encounter Cancelled' : 'Update Encounter'}
    </Button>
  );
};

export default UpdateEncounterButton;
